import { useCurrentDate, Button, DataButton, H5 } from "@sellar-io/components";
import type {
  BuyerRelationshipListResponseType,
  SupplierDistanceResponseType,
} from "@sellar-io/api-v2/types";
import { InfiniteData, UseInfiniteQueryResult } from "@tanstack/react-query";
import dayjs from "dayjs";
import { getDropStatus } from "@sellar-io/common";
import React, {
  ReactElement,
  useEffect,
  useEffectEvent,
  useMemo,
  useState,
} from "react";
import { useNavigate } from "react-router";
import { twMerge } from "tailwind-merge";
import { useDebounce } from "use-debounce";
import { useMediaQuery } from "usehooks-ts";
import { useShallow } from "zustand/react/shallow";
import {
  Icon,
  ListElement,
  ProfilePicture,
  SpinnerLegacy,
} from "../../../components";
import { Alert } from "../../../components/alert/Alert";
import { BadgePurple } from "../../../components/badge/Badge";
import {
  Dropdown,
  DropdownButtonSlim,
  DropdownItem,
  DropdownItems,
} from "../../../components/dropdown/Dropdown";

import { InputContainer, InputText } from "@sellar-io/components";
import { InfiniteList } from "../../../components/infiniteLoading/InfiniteList";
import { IntersectingInfiniteLoadingButton } from "../../../components/infiniteLoading/IntersectingInfiniteLoadingButton";
import { SellarMap } from "../../../components/map/SellarMap";
import { MarkerEntityType } from "../../../components/map/SellarMap.shared";
import { usePaymentTermsIndicator } from "../../../components/paymentTypeIndicator/usePaymentTermsIndicator";
import { SupplierAddToListCard } from "../../../components/supplierAddToList/SupplierAddToListCard";
import { Switch } from "@sellar-io/components";
import {
  BuyerRelationshipsSearchValues,
  BuyerRelationshipsSortValues,
  useBuyerRelationshipsStore,
} from "./buyerRelationshipsStore";
import { DISCOVER, ORDER_BUYER } from "../../../constants/routes";
import { useEvents, useSellarUser } from "../../../hooks";
import { useDiscoverySupplierList } from "../../../hooks/api/v2/discovery/useDiscoverySupplierList";
import { useBuyerRelationshipCount } from "../../../hooks/api/v2/buyerRelationship/useBuyerRelationshipCount";
import { useBuyerRelationshipList } from "../../../hooks/api/v2/buyerRelationship/useBuyerRelationshipList";
import { useUser } from "../../../hooks/api/v2/user/useUser";
import { useUserUpdate } from "../../../hooks/api/v2/user/useUserUpdate";
import { useFeatureFlagStore } from "../../../hooks/useFeatureFlags";
import { useNavigateToChild } from "../../../hooks/useParentNavigation";
import { useVariableSupplierDistanceQuery } from "../../../hooks/useVariableSupplierDistanceQuery";
import { formatDate } from "../../../utils/formatDate";
import { DiscoverySupplierCardCompact } from "../../discovery/v2/components/DiscoverySupplierCardCompact";
import { getNextDirectDeliveryDate } from "../../order/helpers/DeliveryDateHelpers/getNextDeliveryDate";
import { CreateConnectionPaymentTermsRequestDialog } from "./Dialogs/CreateConnectionPaymentTermsRequestDialog";
import { NonConnectedSupplierList } from "./NonConnectedSuppliersList/NonConnectedSupplierList";
import { BuyerRelationshipsTable } from "./SupplierTable/BuyerRelationshipsTable";
import { useSupplierListStore } from "./SupplierTable/SupplierListStore";
import { SellarCreditCheckBanner } from "../../sellarCredit/components/SellarCreditCheckBanner";
import { useSellarCurrentBusiness } from "../../../hooks/useSellarCurrentBusiness.shared";
import { useRemoteFeatureFlags } from "../../../hooks/useRemoteFeatureFlags";
import { useDiscoveryDistributionSupplierList } from "../../../hooks/api/v2/discovery/useDiscoveryDistributionSupplierList";
import { DiscoveryV2DistributionSuppliersBanner } from "../../discovery/v2/distribution/DiscoveryV2DistributionSuppliersBanner";
import { DiscoveryV2DistributionSuppliersBannerV2Container } from "../../discovery/v2/distribution/DiscoveryV2DistributionSuppliersBannerV2";
import { useDiscoveryV2DistributionTracking } from "../../discovery/v2/distribution/useDiscoveryV2DistributionTracking";
import { useDistributionDeliveryEstimate } from "~/hooks/useDeliveryEstimates/useDistributionDeliveryEstimate";
import { BuyerRelationshipDeliveryEstimateProvider } from "./BuyerRelationshipDeliveryEstimate/BuyerRelationshipDeliveryEstimateProvider";
import { zLayer } from "@sellar-io/components";

export function BuyerRelationships({
  onOpenDraftBasketDrawer,
}: {
  onOpenDraftBasketDrawer: () => void;
}): ReactElement {
  const isMobile = useMediaQuery("(max-width: 767px)");
  const [showMap, setShowMap] = useState<boolean>(false);
  const { track } = useEvents();
  const { search, sort, isFavourite } = useSupplierListStore(
    useShallow((state) => ({
      search: state.search,
      sort: state.sort,
      setIsFavourite: state.setIsFavourite,
      isFavourite: state.isFavourite,
    })),
  );
  const { columnSort } = useBuyerRelationshipsStore(
    useShallow((state) => ({
      columnSort: state.columnSortName,
    })),
  );

  const [debouncedSearchValue] = useDebounce(search, 500);

  const buyerRelationshipListQuery = useBuyerRelationshipList({
    includeAnalytics: !showMap,
    query: [
      ...(debouncedSearchValue
        ? BuyerRelationshipsSearchValues["name"].mutation(debouncedSearchValue)
        : []),
      ...(isFavourite
        ? ([
            {
              type: "isFavourite",
              modifier: "EQUALS",
              value: isFavourite,
            },
          ] as const)
        : []),
    ],
    ...(!showMap
      ? {
          limit: 20,
          search: debouncedSearchValue,
          sort: [
            BuyerRelationshipsSortValues[isMobile ? sort : columnSort].mutation,
          ],
        }
      : {
          limit: 100,
          search: debouncedSearchValue,
        }),
  });

  const buyerRelationshipCountQuery = useBuyerRelationshipCount(
    {
      query: [
        ...(debouncedSearchValue
          ? BuyerRelationshipsSearchValues["name"].mutation(
              debouncedSearchValue,
            )
          : []),
      ],
    },
    { enabled: !isMobile && !showMap },
  );

  const buyerRelationshipSupplierIdsByPage = useMemo(
    () =>
      (buyerRelationshipListQuery.data?.pages ?? []).map((page) =>
        page.map((relationship) => relationship.Relationship.SupplierId),
      ),
    [buyerRelationshipListQuery.data?.pages],
  );

  const isLoading = buyerRelationshipListQuery.isPending;

  const trackSupplierListViewed = useEffectEvent(() => {
    track("Supplier List Viewed");
  });

  useEffect(() => {
    trackSupplierListViewed();
  }, []);

  const buyerRelationshipCount = buyerRelationshipCountQuery.data;

  return (
    <div className="flex w-full flex-col">
      <BuyerRelationshipDeliveryEstimateProvider
        buyerRelationshipSupplierIdsByPage={buyerRelationshipSupplierIdsByPage}
      >
        <Content
          showMap={showMap}
          isMobile={isMobile}
          setShowMap={setShowMap}
          buyerRelationshipListQuery={buyerRelationshipListQuery}
          connectionCount={buyerRelationshipCount}
          isLoading={isLoading}
          debouncedSearchValue={debouncedSearchValue}
          onOpenDraftBasketDrawer={onOpenDraftBasketDrawer}
        />
      </BuyerRelationshipDeliveryEstimateProvider>
      <div className="px-4">
        <div className="mx-auto w-full max-w-[1300px] justify-center">
          <MoreSuppliersContent
            buyerRelationshipCount={buyerRelationshipCount}
          />
          {debouncedSearchValue && <NonConnectedSupplierList />}
        </div>
      </div>
    </div>
  );
}

const MoreSuppliersContent = ({
  buyerRelationshipCount,
}: {
  buyerRelationshipCount: number | undefined;
}) => {
  const { search } = useSupplierListStore(
    useShallow((state) => ({
      search: state.search,
    })),
  );
  const [showMore, setShowMore] = useState<boolean>(false);
  const showPowerByBanner =
    !!buyerRelationshipCount && buyerRelationshipCount > 5;
  return (
    <>
      {showPowerByBanner && !search && (
        <Alert
          title={"Looking for more?"}
          message={
            "Sellar Market is home to 100s of the UK's best craft suppliers. Add them to your supplier list for easy access to their pricing and availability"
          }
          type={"sellar"}
          className={"mt-4 w-full bg-white"}
        >
          <Button
            color={"tertiary"}
            onClick={() => setShowMore((prevShowMore) => !prevShowMore)}
          >
            {showMore ? (
              <>
                Show less <Icon icon={"CHEVRON_UP"} />
              </>
            ) : (
              <>
                Show more <Icon icon={"CHEVRON_RIGHT"} />
              </>
            )}
          </Button>
        </Alert>
      )}
      {(showMore || !showPowerByBanner) && !search && (
        <>
          <NearbySuppliersList />
          <MoreOnTheMarket />
        </>
      )}
    </>
  );
};

const Content = ({
  isMobile,
  buyerRelationshipListQuery,
  isLoading,
  connectionCount,
  showMap,
  setShowMap,
  debouncedSearchValue,
  onOpenDraftBasketDrawer,
}: {
  isMobile: boolean;
  buyerRelationshipListQuery: UseInfiniteQueryResult<
    InfiniteData<BuyerRelationshipListResponseType>
  >;
  isLoading: boolean;
  connectionCount: number | undefined;
  showMap: boolean;
  setShowMap: (showMap: boolean) => void;
  debouncedSearchValue: string;
  onOpenDraftBasketDrawer: () => void;
}) => {
  const { onTrackSupplierClick, onTrackBannerClick } =
    useDiscoveryV2DistributionTracking();
  const { isActive } = useRemoteFeatureFlags();
  const sdConsolidatedCheckoutEnabled = isActive("sdConsolidatedCheckout");

  const addToFavouritesActive = isActive("inAppNotifications");
  const { setSort, search, isFavourite, setIsFavourite } = useSupplierListStore(
    useShallow((state) => ({
      setSort: state.setSort,
      search: state.search,
      isFavourite: state.isFavourite,
      setIsFavourite: state.setIsFavourite,
    })),
  );
  const { user } = useSellarUser();
  const { business } = useSellarCurrentBusiness();
  const buyerRelationshipRecentlyAddedQuery = useBuyerRelationshipList({
    query: [
      ...(debouncedSearchValue
        ? BuyerRelationshipsSearchValues["name"].mutation(debouncedSearchValue)
        : []),
      {
        type: "recentlyAdded" as const,
        modifier: "EQUALS" as const,
        value: true,
      },
    ],
    sort: [
      {
        type: "recentlyAdded",
        direction: "desc",
      },
    ],
    limit: 10,
  });

  const buyerRelationshipStorefrontScheduledReleaseQuery =
    useBuyerRelationshipList({
      query: [
        ...(debouncedSearchValue
          ? BuyerRelationshipsSearchValues["name"].mutation(
              debouncedSearchValue,
            )
          : []),
        {
          type: "hasScheduledStorefrontRelease" as const,
          modifier: "EQUALS" as const,
          value: true,
        },
      ],
      sort: [
        {
          type: "storefrontReleaseDate",
          direction: "asc",
        },
      ],
      limit: 10,
    });

  const highlightedSuppliersList = Array.from(
    new Map(
      [
        ...(buyerRelationshipStorefrontScheduledReleaseQuery.data?.pages[0] ??
          []),
        ...(buyerRelationshipRecentlyAddedQuery.data?.pages[0] ?? []),
      ].map((supplier) => [supplier.id, supplier]),
    ).values(),
  );

  const buyerPostcode = business?.DeliveryAddress.postalCode ?? "";

  const discoveryDistributionSupplierListQuery =
    useDiscoveryDistributionSupplierList(
      {
        query: [
          {
            type: "retailerPostalCode",
            modifier: "EQUALS",
            value: buyerPostcode,
          },
        ],
        limit: 1,
      },
      {
        enabled: Boolean(buyerPostcode),
      },
    );

  const distributionSuppliers =
    discoveryDistributionSupplierListQuery.data?.pages.flat() ?? [];

  const hasDistributionSuppliers = distributionSuppliers.length > 0;

  const hasHighlightedSuppliersList =
    highlightedSuppliersList.length > 0 && !hasDistributionSuppliers;

  return (
    <>
      {isMobile ? (
        <>
          {hasDistributionSuppliers && (
            <>
              {sdConsolidatedCheckoutEnabled ? (
                <DiscoveryV2DistributionSuppliersBannerV2Container
                  supplierListVisibility="visible-when-inactive"
                  className="p-4"
                  onTrackSupplierClick={({ supplierName }) => {
                    onTrackSupplierClick({
                      supplierName,
                      source: "suppliers-list",
                    });
                  }}
                  onTrackBannerClick={() => {
                    onTrackBannerClick({
                      source: "suppliers-list",
                    });
                  }}
                  onOpenDbsDraftBasketDrawer={() => onOpenDraftBasketDrawer()}
                />
              ) : (
                <DiscoveryV2DistributionSuppliersBanner
                  className="p-4"
                  onTrackSupplierClick={({ supplierName }) => {
                    onTrackSupplierClick({
                      supplierName,
                      source: "suppliers-list",
                    });
                  }}
                  onTrackBannerClick={() => {
                    onTrackBannerClick({
                      source: "suppliers-list",
                    });
                  }}
                />
              )}
            </>
          )}
          <SellarCreditCheckBanner className="mt-4 px-4" />

          <div
            className={twMerge(
              `sticky top-[var(--nav-height)] ${zLayer.pinned} flex w-full flex-col gap-2 border-t border-b bg-neutral-100 p-4`,
            )}
          >
            <div className="flex items-center gap-2">
              <div className="flex w-full">
                <BuyerConnectionSearchInput />
              </div>
              <Dropdown>
                <DropdownButtonSlim>
                  <Icon icon={"SWAP_VERTICAL"} />
                </DropdownButtonSlim>
                <DropdownItems>
                  {Object.values(BuyerRelationshipsSortValues).map(
                    (sortValue) => (
                      <DropdownItem
                        key={sortValue.key}
                        onClick={() => setSort(sortValue.key)}
                      >
                        {sortValue.name}
                      </DropdownItem>
                    ),
                  )}
                </DropdownItems>
              </Dropdown>
              <DataButton border={true} onClick={() => setShowMap(!showMap)}>
                {showMap ? (
                  <>
                    <Icon icon={"FORMAT_LIST_BULLETED"} />
                  </>
                ) : (
                  <>
                    <Icon icon={"MAP"} />
                  </>
                )}
              </DataButton>
            </div>
            {addToFavouritesActive && (
              <div className="flex flex-row gap-2">
                <p>Favourite suppliers</p>
                <Switch
                  checked={isFavourite}
                  onChange={() => setIsFavourite(!isFavourite)}
                />
              </div>
            )}
          </div>
          {hasHighlightedSuppliersList && (
            <div className={"p-4"}>
              <HighlightedSuppliersList
                highlightSuppliersList={highlightedSuppliersList}
              />
            </div>
          )}
          {showMap ? (
            <SupplierListMapview
              buyerRelationshipListQuery={buyerRelationshipListQuery}
            />
          ) : (
            <>
              <BuyerConnectionSupplierListMobile
                buyerRelationshipListQuery={buyerRelationshipListQuery}
              />
              {!isLoading &&
                !buyerRelationshipListQuery.data?.pages[0]?.[0] && (
                  <p className="p-3">
                    {search || isFavourite
                      ? "No results found in your supplier list"
                      : "Suppliers that you connect with on Sellar will appear here"}
                  </p>
                )}
            </>
          )}
        </>
      ) : (
        <>
          {hasDistributionSuppliers && (
            <>
              {sdConsolidatedCheckoutEnabled ? (
                <DiscoveryV2DistributionSuppliersBannerV2Container
                  supplierListVisibility="visible-when-inactive"
                  contentClassName="max-w-325 py-4 px-0"
                  className="px-4"
                  onTrackSupplierClick={({ supplierName }) => {
                    onTrackSupplierClick({
                      supplierName,
                      source: "suppliers-list",
                    });
                  }}
                  onTrackBannerClick={() => {
                    onTrackBannerClick({
                      source: "suppliers-list",
                    });
                  }}
                  onOpenDbsDraftBasketDrawer={() => onOpenDraftBasketDrawer()}
                />
              ) : (
                <DiscoveryV2DistributionSuppliersBanner
                  contentClassName="max-w-325 py-4 px-0"
                  className="px-4"
                  onTrackSupplierClick={({ supplierName }) => {
                    onTrackSupplierClick({
                      supplierName,
                      source: "suppliers-list",
                    });
                  }}
                  onTrackBannerClick={() => {
                    onTrackBannerClick({
                      source: "suppliers-list",
                    });
                  }}
                />
              )}
            </>
          )}
          <div className={"px-4"}>
            <div className="mx-auto mt-0 max-w-325">
              <SellarCreditCheckBanner className="mt-4" />
              <div
                className={twMerge(
                  `sticky top-[var(--nav-height)] ${zLayer.pinned} flex flex-col gap-4 bg-neutral-100 pt-4`,
                  hasHighlightedSuppliersList && "pt-4",
                )}
              >
                {user?.id && <CreditTermsRequestAlert userId={user.id} />}
                <div
                  className={twMerge(
                    "flex w-full border-collapse flex-row justify-between border-b border-zinc-300 bg-zinc-50 p-4",
                    "rounded-t-lg border border-b-0",
                    hasHighlightedSuppliersList && "rounded-lg border-b",
                  )}
                >
                  <BuyerConnectionSearchInput />
                  <div className="flex flex-row items-center gap-2">
                    {addToFavouritesActive && (
                      <>
                        <p>Favourite suppliers</p>
                        <Switch
                          checked={isFavourite}
                          onChange={() => setIsFavourite(!isFavourite)}
                        />
                      </>
                    )}
                    <DataButton
                      border={true}
                      onClick={() => setShowMap(!showMap)}
                    >
                      {showMap ? (
                        <>
                          <Icon icon={"FORMAT_LIST_BULLETED"} />
                          List view
                        </>
                      ) : (
                        <>
                          <Icon icon={"MAP"} />
                          Map view
                        </>
                      )}
                    </DataButton>
                  </div>
                </div>
              </div>
              {hasHighlightedSuppliersList && (
                <div className={"py-4"}>
                  <HighlightedSuppliersList
                    highlightSuppliersList={highlightedSuppliersList}
                  />
                </div>
              )}
              {showMap ? (
                <SupplierListMapview
                  buyerRelationshipListQuery={buyerRelationshipListQuery}
                />
              ) : (
                <div
                  className={twMerge(
                    hasHighlightedSuppliersList && "rounded-t-lg",
                    "sticky top-[104px] flex flex-col gap-4 bg-neutral-100",
                  )}
                >
                  <BuyerRelationshipsTable
                    buyerRelationshipListQuery={buyerRelationshipListQuery}
                    connectionCount={connectionCount}
                  />
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </>
  );
};

const CreditTermsRequestAlert = ({ userId }: { userId: number }) => {
  const { isActive } = useFeatureFlagStore();
  const userQuery = useUser(userId);
  const userUpdateMutation = useUserUpdate(userId);
  const user = userQuery.data;
  const [hideAlert, setHideAlert] = useState<boolean>(true);
  const [previousViewedState, setPreviousViewedState] = useState(
    user?.creditTermsRequestViewed,
  );
  if (user?.creditTermsRequestViewed !== previousViewedState) {
    setPreviousViewedState(user?.creditTermsRequestViewed);
    if (user) setHideAlert(user.creditTermsRequestViewed);
  }
  const updateUser = () => {
    setHideAlert(true);
    return userUpdateMutation.mutate(
      {
        data: {
          creditTermsRequestViewed: true,
        },
      },
      {
        onError: () => setHideAlert(false),
      },
    );
  };

  if (!isActive("CHECKOUT_CREDIT_TERMS_REQUEST")) return null;
  return (
    <>
      {user && !hideAlert && (
        <Alert
          type={"sellar"}
          title={"NEW! Request your correct credit terms from your supplier"}
          message={
            "If you're not seeing your usual credit terms with a Supplier, you can now request them directly from your supplier list"
          }
          className={"w-full bg-zinc-50"}
          onDismiss={updateUser}
        />
      )}
    </>
  );
};

const HighlightedSuppliersList = ({
  highlightSuppliersList,
}: {
  highlightSuppliersList: BuyerRelationshipListResponseType;
}) => {
  const currentDate = useCurrentDate();
  const { isActive } = useRemoteFeatureFlags();
  const isImplicitProformaActive = isActive("implicitProforma");

  return (
    <div className="flex items-center gap-2">
      <div className={"flex h-full gap-3 overflow-auto pb-2"}>
        {highlightSuppliersList.map((buyerRelationship) => {
          const storefrontAvailability =
            buyerRelationship.Relationship.Supplier.StorefrontAvailability;
          const dropStatus = getDropStatus(currentDate, storefrontAvailability);
          const contentType =
            storefrontAvailability.type === "scheduled" &&
            dropStatus !== "ended"
              ? "scheduledStorefront"
              : "recentlyAdded";

          return (
            <HighlightedSuppliersListContent
              key={buyerRelationship.id}
              buyerRelationship={buyerRelationship}
              type={contentType}
              isImplicitProformaActive={isImplicitProformaActive}
            />
          );
        })}
      </div>
    </div>
  );
};

const HighlightedSuppliersListContent = ({
  buyerRelationship,
  type,
  isImplicitProformaActive,
}: {
  buyerRelationship: BuyerRelationshipListResponseType[number];
  type: "recentlyAdded" | "scheduledStorefront";
  isImplicitProformaActive: boolean;
}) => {
  const navigateToChild = useNavigateToChild();
  const supplier = buyerRelationship.Relationship.Supplier;
  const connection = buyerRelationship.Relationship.BuyerRelationshipDetails;
  const { backgroundColor, supplierCard, borderColor } =
    usePaymentTermsIndicator({
      connection: connection
        ? {
            ...connection,
            paymentDue: connection.paymentDue ?? null,
            paymentDueCalculateFrom: connection.paymentDueCalculateFrom ?? null,
            paymentTerms: connection.paymentTerms ?? null,
          }
        : null,
      invoiceCreationStage: supplier.invoiceCreationStage,
      approved: connection?.approved ?? false,
      paymentTermsRequest: connection?.PaymentTermsRequest,
      hasDefaultPriceTier: !!supplier.CustomerZones[0],
      supplier,
    });

  return (
    <DiscoverySupplierCardCompact
      className={"w-[200px] flex-none md:w-[300px]"}
      key={buyerRelationship.id}
      description={
        type === "recentlyAdded" ? (
          <p
            className={
              "overflow-hidden text-sm text-ellipsis whitespace-nowrap text-purple-600 italic"
            }
          >
            Added {dayjs(buyerRelationship.createdAt).fromNow()}
          </p>
        ) : (
          <p className="text-sm text-zinc-700">Sellar imports • Limited drop</p>
        )
      }
      supplier={{
        ...supplier,
        containerTypes: [],
      }}
      footerClassName={`${borderColor} ${backgroundColor}`}
      footer={isImplicitProformaActive ? null : supplierCard}
      onClick={() =>
        navigateToChild(
          ORDER_BUYER.href({ supplierIdOrUrlId: supplier.id.toString() }),
        )
      }
    />
  );
};

const SupplierListMapview = ({
  buyerRelationshipListQuery,
}: {
  buyerRelationshipListQuery: UseInfiniteQueryResult<
    InfiniteData<BuyerRelationshipListResponseType>
  >;
}) => {
  if (buyerRelationshipListQuery.isPending) return <SpinnerLegacy />;
  const supplierRelationshipsFlat: MarkerEntityType[] = (
    buyerRelationshipListQuery.data?.pages.flatMap((page) => page) ?? []
  ).map((buyerRelationship) => ({
    ...buyerRelationship.Relationship.Supplier,
    Business: buyerRelationship.Relationship.Supplier.Business,
    SupplierRetailerConnectionId:
      buyerRelationship.Relationship.BuyerRelationshipDetails?.id,
  }));

  return (
    <div className="h-[60vh] w-full">
      <SellarMap
        entities={supplierRelationshipsFlat.map((connection) => connection)}
        displaying="supplier"
      />
    </div>
  );
};

const BuyerConnectionSearchInput = () => {
  const { search, setSearch } = useSupplierListStore(
    useShallow((state) => ({
      search: state.search,
      setSearch: state.setSearch,
    })),
  );
  const isMobile = useMediaQuery("(max-width: 767px)");
  return (
    <InputContainer
      className={twMerge("py-[0.4rem]", isMobile ? "w-full" : "w-1/2")}
    >
      <InputText
        required={false}
        placeholder={`Search for suppliers by name...`}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />
    </InputContainer>
  );
};

const BuyerConnectionSupplierListMobile = ({
  buyerRelationshipListQuery,
}: {
  buyerRelationshipListQuery: UseInfiniteQueryResult<
    InfiniteData<BuyerRelationshipListResponseType>
  >;
}) => {
  return (
    <InfiniteList query={buyerRelationshipListQuery}>
      {!!buyerRelationshipListQuery.data?.pages[0]?.length &&
        buyerRelationshipListQuery.data.pages.map((page) =>
          page.map((buyerRelationship) => (
            <BuyerConnectionSupplierListMobileItem
              buyerRelationship={buyerRelationship}
              key={buyerRelationship.id}
            />
          )),
        )}
    </InfiniteList>
  );
};

const BuyerConnectionSupplierListMobileItem = ({
  buyerRelationship,
}: {
  buyerRelationship: BuyerRelationshipListResponseType[number];
}) => {
  const currentDate = useCurrentDate();
  const { isActive: isFeatureFlagActive } = useFeatureFlagStore();
  const { isActive: isRemoteFeatureFlagActive } = useRemoteFeatureFlags();
  const isImplicitProformaActive =
    isRemoteFeatureFlagActive("implicitProforma");
  const [active, setActive] = useState<boolean>(false);
  const navigateToChild = useNavigateToChild();
  const connection = buyerRelationship.Relationship.BuyerRelationshipDetails;
  const supplier = buyerRelationship.Relationship.Supplier;
  const paymentTermsRequest = connection?.PaymentTermsRequest;
  const { type, fullTextOnly, mainPaymentOption, textColor } =
    usePaymentTermsIndicator({
      connection: {
        ...connection,
        paymentDue: connection?.paymentDue ?? null,
        paymentDueCalculateFrom: connection?.paymentDueCalculateFrom ?? null,
        paymentTerms: connection?.paymentTerms ?? null,
      },
      invoiceCreationStage: supplier.invoiceCreationStage,
      approved: connection?.approved ?? false,
      paymentTermsRequest: connection?.PaymentTermsRequest,
      hasDefaultPriceTier: !!supplier.CustomerZones[0],
      supplier,
    });

  const { business } = useSellarCurrentBusiness();

  const buyerCreditFlagEnabled = isRemoteFeatureFlagActive(
    "retailerSellarCreditCheckout",
  );

  const buyerPostcode = business?.DeliveryAddress.postalCode ?? "";
  const supplierId = buyerRelationship.Relationship.SupplierId;

  const { nextDeliveryDate: sdNextDeliveryDate } =
    useDistributionDeliveryEstimate({
      supplierId,
      productIds: [],
      buyerPostcode,
    });

  const showAdditionalPaymentMethods =
    business?.BusinessUser?.canAccessSellarCredit &&
    !supplier.hasOptedOutSellarCredit &&
    buyerCreditFlagEnabled;

  const isFavourited = !!buyerRelationship.BuyerRelationshipFavourite;

  const getNumberOfPaymentOptions = () => {
    let number = 1;
    if (supplier.stripeAccountId) number++;
    return number;
  };

  const canEdit =
    !paymentTermsRequest &&
    type !== "termsRequested" &&
    !!connection &&
    isFeatureFlagActive("CHECKOUT_CREDIT_TERMS_REQUEST");
  const navigateToSupplier = () => {
    return navigateToChild(
      ORDER_BUYER.href({ supplierIdOrUrlId: supplier.id.toString() }),
    );
  };

  const renderDelivery = () => {
    if (connection?.fulfillmentType === "direct" && supplier.DeliveryArea) {
      return (
        <p>
          Direct: Next Delivery:{" "}
          <strong>
            {formatDate(
              getNextDirectDeliveryDate({
                deliveryArea: supplier.DeliveryArea,
                startDateReference: currentDate,
              })?.deliveryDate ?? null,
            )}
          </strong>
        </p>
      );
    }

    if (connection?.fulfillmentType === "sellarDistribution") {
      return `Delivered by Sellar: ${formatDate(sdNextDeliveryDate)}`;
    }

    return <p>{connection?.fulfillmentType}</p>;
  };

  return (
    <>
      <ListElement key={buyerRelationship.id} icon={null}>
        <div className={"flex w-full flex-col gap-2"}>
          <div
            className={
              "flex w-full items-center gap-2 rounded-lg border border-zinc-300 bg-zinc-50 p-2"
            }
            onClick={navigateToSupplier}
          >
            <figure className="relative">
              <ProfilePicture
                name={supplier.Business.name}
                logoUrl={supplier.Business.logoUrl}
              />
            </figure>
            <div>
              <H5>{supplier.Business.name}</H5>
              <span>
                {supplier.Business.DeliveryAddress.city},{" "}
                {supplier.Business.DeliveryAddress.postalCode}
              </span>
            </div>
            <div className="ml-auto flex items-center gap-1">
              {isFavourited && (
                <>
                  <div className="relative m-1 flex h-6 w-6 items-center justify-center rounded-xl bg-purple-100 outline outline-1 outline-offset-[-1px] outline-purple-100">
                    <Icon
                      className={"text-purple-800"}
                      size={0.7}
                      icon={"HEART"}
                    />
                  </div>
                </>
              )}
              <Icon icon="CHEVRON_RIGHT" />
            </div>
          </div>
          <div className="flex py-2" onClick={navigateToSupplier}>
            {dayjs().diff(
              dayjs(buyerRelationship.createdAt).toDate(),
              "weeks",
              true,
            ) <= 4 && (
              <BadgePurple icon={"STAR"}>
                Added {dayjs(buyerRelationship.createdAt).fromNow()}
              </BadgePurple>
            )}
          </div>
          <div className="grid grid-cols-[max-content_1fr] gap-x-14 gap-y-2">
            {(!isImplicitProformaActive || type === "credit") && (
              <>
                <H5 className="font-semibold">Terms</H5>
                <div
                  className={twMerge(
                    "inline-flex items-center gap-2",
                    textColor && textColor,
                  )}
                  onClick={() =>
                    canEdit ? setActive(true) : navigateToSupplier()
                  }
                >
                  <p>
                    {isImplicitProformaActive
                      ? fullTextOnly
                      : mainPaymentOption}{" "}
                    {showAdditionalPaymentMethods &&
                      `(+${getNumberOfPaymentOptions()})`}
                  </p>
                  {canEdit && <Icon icon={"PENCIL"} />}
                </div>
              </>
            )}
            <H5 onClick={navigateToSupplier} className="font-semibold">
              Delivery
            </H5>
            <span onClick={navigateToSupplier} className="capitalize">
              {renderDelivery()}
            </span>
            <H5 onClick={navigateToSupplier} className="font-semibold">
              Last ordered
            </H5>
            <span onClick={navigateToSupplier}>
              {connection?.lastOrderedAt
                ? dayjs(connection.lastOrderedAt).format("DD/MM/YYYY")
                : "Yet to order"}
            </span>
          </div>
        </div>
      </ListElement>
      {connection && (
        <CreateConnectionPaymentTermsRequestDialog
          active={active}
          onDismiss={() => setActive(false)}
          connectionId={connection.id}
          supplierName={supplier.Business.name}
        />
      )}
    </>
  );
};

const NearbySuppliersList = () => {
  const { suppliers } = useVariableSupplierDistanceQuery({
    limit: 10,
  });

  const [localSuppliers, setLocalSuppliers] =
    useState<SupplierDistanceResponseType>();

  const { user } = useSellarUser();
  const userId = user?.id;

  if (suppliers.length && suppliers !== localSuppliers) {
    setLocalSuppliers(suppliers);
  }

  if (!localSuppliers?.length) return null;
  if (!userId) return null;
  return (
    <>
      <div className={"mt-8"}>
        <H5 className={"mb-4"}>Other suppliers you may know </H5>
        <div className="mt-4 grid gap-2 md:grid-cols-2 lg:grid-cols-5">
          {localSuppliers.map((supplier, index) => (
            <NearbySupplierContent
              key={supplier.id}
              supplier={supplier}
              index={index}
              userId={userId}
            />
          ))}
        </div>
      </div>
    </>
  );
};

const NearbySupplierContent = ({
  supplier,
  index,
  userId,
}: {
  supplier: SupplierDistanceResponseType[number];
  index: number;
  userId: number;
}) => {
  return (
    <div className={"col-span-1"}>
      <SupplierAddToListCard
        supplier={supplier}
        userId={userId}
        index={index}
        canRemove={true}
        tooltipEnabled={true}
      />
    </div>
  );
};

const MoreOnTheMarket = () => {
  const navigate = useNavigate();
  const suppliersQuery = useDiscoverySupplierList({
    limit: 20,
    query: [
      {
        type: "hideRelationshipSuppliers",
        modifier: "EQUALS",
        value: true,
      },
    ],
    orderBy: "random",
  });
  const { user } = useSellarUser();
  const userId = user?.id;

  // if (!suppliersQuery.data?.pages?.[0]?.length) return null;
  if (!userId) return null;
  return (
    <>
      <div className={"mt-8"}>
        <H5 className={"mb-4"}>More on the Market </H5>
        {suppliersQuery.isLoading ? (
          <SpinnerLegacy />
        ) : (
          <>
            <div className="mt-4 grid gap-2 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5">
              {suppliersQuery.data?.pages.map((page) =>
                page.map((supplier, index) => (
                  <SupplierAddToListCard
                    key={supplier.id}
                    supplier={supplier}
                    userId={userId}
                    index={index}
                  />
                )),
              )}
              <IntersectingInfiniteLoadingButton query={suppliersQuery} />
            </div>
          </>
        )}
        <div className="mt-4 flex justify-center">
          <Button onClick={() => navigate(DISCOVER)}>
            See more on the market
          </Button>
        </div>
      </div>
    </>
  );
};
